import './agenda-block-list-item.scss';

import { kebabCase } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

import { AppointmentModalData } from '../../../data/data/types';
import {
  AgendaBlockItemDataFragment,
  SlotType,
} from '../../../data/types/api/graphql';
import {
  formatTimeSpanToTime,
  getTimeSpanMinutes,
} from '../../../data/utils/date.utils';
import { makeKeyDownHandler } from '../../../data/utils/make-key-down-handler';
import { CustomFormattedMessage } from '../../../elements/intl/custom-formatted-message';
import { AgendaAppointmentSlotList } from './agenda-appointment-slot-list';

type AgendaBlockListItemProps = {
  compact?: boolean;
  healthProviderId: string | null | undefined;
  healthProfessionalId: string | null | undefined;
  date: string;
  agendaBlockItem: AgendaBlockItemDataFragment;
  showAppointmentModal?: (
    slotType: SlotType,
    value: AppointmentModalData | null
  ) => void;
};

export function AgendaBlockListItem({
  compact,
  healthProviderId,
  healthProfessionalId,
  date,
  agendaBlockItem,
  showAppointmentModal,
}: AgendaBlockListItemProps) {
  const [active, setActive] = useState<boolean | null>(null);

  const activeFallback = useMemo(() => {
    if (active === null) {
      const currentDate = new Date(date);

      currentDate.setHours(
        0,
        getTimeSpanMinutes(agendaBlockItem.template.minuteEndTime),
        0,
        0
      );

      if (new Date() > currentDate) {
        return (
          agendaBlockItem.slots.filter(slot => {
            const appointments = slot.appointments ?? [];

            if (appointments.length > 0) {
              return (
                appointments.filter(appointment => !appointment.isCompleted)
                  .length > 0
              );
            }

            return false;
          }).length !== 0
        );
      }

      return true;
    }

    return active;
  }, [active, date, agendaBlockItem]);

  const onHandleToggle = useCallback(() => {
    setActive(prevState => !prevState);
  }, []);

  return (
    <div
      className={`agenda-appointment-block-item${
        activeFallback ? ' active' : ''
      }${compact ? ' compact' : ''}`}
    >
      <div
        className="agenda-appointment-block-item__header"
        role="button"
        tabIndex={0}
        onClick={onHandleToggle}
        onKeyDown={makeKeyDownHandler(onHandleToggle)}
      >
        <h2>
          {agendaBlockItem.corrupt ? (
            <CustomFormattedMessage
              id="template.entry-type-corrupt.title"
              values={{
                name: agendaBlockItem.template.name,
                minutes: agendaBlockItem.template.minuteLength,
              }}
            />
          ) : (
            <>
              {formatTimeSpanToTime(agendaBlockItem.template.minuteStartTime)} -{' '}
              {formatTimeSpanToTime(agendaBlockItem.template.minuteEndTime)}:{' '}
              <CustomFormattedMessage
                id={`template.entry-type-${kebabCase(
                  agendaBlockItem.template.type
                )}.title`}
                values={{
                  name: agendaBlockItem.template.name,
                  minutes: agendaBlockItem.template.minuteLength,
                }}
              />
            </>
          )}
        </h2>
      </div>

      {activeFallback && (
        <div className="agenda-appointment-block-item__content">
          <AgendaAppointmentSlotList
            compact={compact}
            healthProviderId={healthProviderId}
            healthProfessionalId={healthProfessionalId}
            agendaBlockItem={agendaBlockItem}
            showAppointmentModal={showAppointmentModal}
          />
        </div>
      )}
    </div>
  );
}
